import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Coffee, Sparkles, Camera, Calendar, LogOut, Settings, Heart, Lightbulb, Home, Gift } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/vd09zrou_Logo%20-%20Final.png";

const adminSections = [
  { to: '/admin/homepage', label: 'Homepage', icon: Home, color: 'bg-gradient-to-r from-sip-blue to-sip-pink', description: 'Customize homepage images' },
  { to: '/admin/locations', label: 'Locations', icon: MapPin, color: 'bg-sip-cyan', description: 'Manage schedule & locations' },
  { to: '/admin/menu', label: 'Menu', icon: Coffee, color: 'bg-sip-purple', description: 'Manage menu items' },
  { to: '/admin/seasonal', label: 'Seasonal', icon: Sparkles, color: 'bg-sip-pink', description: 'Manage seasonal specials' },
  { to: '/admin/photos', label: 'Photos', icon: Camera, color: 'bg-sip-orange', description: 'Manage photo gallery' },
  { to: '/admin/community', label: 'Community', icon: Heart, color: 'bg-sip-red', description: 'Manage community involvement' },
  { to: '/admin/flavors', label: 'Flavors', icon: Lightbulb, color: 'bg-gradient-to-r from-sip-orange to-sip-pink', description: 'Review flavor submissions' },
  { to: '/admin/bookings', label: 'Bookings', icon: Calendar, color: 'bg-sip-yellow', description: 'View event booking requests' },
  { to: '/admin/loyalty', label: 'Loyalty', icon: Gift, color: 'bg-gradient-to-r from-sip-yellow to-sip-orange', description: 'Manage customer rewards' },
];

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ locations: 0, menu: 0, seasonal: 0, photos: 0, bookings: 0, community: 0, flavors: 0 });

  const fetchUser = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const [locRes, menuRes, seasonalRes, photosRes, bookingsRes, communityRes, flavorsRes] = await Promise.all([
        fetch(`${API}/locations`),
        fetch(`${API}/menu`),
        fetch(`${API}/seasonal`),
        fetch(`${API}/photos`),
        fetch(`${API}/bookings`, { credentials: 'include' }),
        fetch(`${API}/community`),
        fetch(`${API}/admin/flavors`, { credentials: 'include' })
      ]);

      const [locations, menu, seasonal, photos, bookings, community, flavors] = await Promise.all([
        locRes.json(),
        menuRes.json(),
        seasonalRes.json(),
        photosRes.json(),
        bookingsRes.ok ? bookingsRes.json() : [],
        communityRes.json(),
        flavorsRes.ok ? flavorsRes.json() : []
      ]);

      setStats({
        locations: locations.length,
        menu: menu.length,
        seasonal: seasonal.length,
        photos: photos.length,
        bookings: bookings.length,
        community: community.length,
        flavors: flavors.filter(f => !f.is_approved).length // Show pending count
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchUser();
    fetchStats();
  }, [fetchUser, fetchStats]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { 
        method: 'POST',
        credentials: 'include' 
      });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="Sip Fantasia" className="h-12 w-auto" />
            </Link>
            
            <div className="flex items-center gap-4">
              {user && (
                <div className="flex items-center gap-3">
                  {user.picture && (
                    <img 
                      src={user.picture} 
                      alt={user.name}
                      className="w-8 h-8 rounded-full border-2 border-sip-purple"
                    />
                  )}
                  <span className="text-gray-700 text-sm hidden sm:block">
                    {user.name}
                  </span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-sip-purple text-white font-medium rounded-full hover:bg-sip-purple/90 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <Settings className="w-8 h-8 text-sip-purple" />
            <h1 className="font-script text-3xl md:text-4xl text-gray-900">
              Admin Dashboard
            </h1>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            {[
              { label: 'Locations', value: stats.locations, color: 'bg-sip-cyan' },
              { label: 'Menu Items', value: stats.menu, color: 'bg-sip-purple' },
              { label: 'Seasonal', value: stats.seasonal, color: 'bg-sip-pink' },
              { label: 'Photos', value: stats.photos, color: 'bg-sip-orange' },
              { label: 'Community', value: stats.community, color: 'bg-sip-red' },
              { label: 'Pending Flavors', value: stats.flavors, color: 'bg-gradient-to-r from-sip-orange to-sip-pink' },
              { label: 'Bookings', value: stats.bookings, color: 'bg-sip-yellow' },
            ].map((stat, index) => (
              <div 
                key={stat.label}
                className={`${stat.color} p-4 rounded-2xl text-center`}
                data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
              >
                <p className="font-bold text-3xl text-white">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Admin Sections */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminSections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={section.to}
                    className="card-graffiti block group"
                    data-testid={`admin-section-${section.label.toLowerCase()}`}
                  >
                    <div className={`${section.color} w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={24} />
                    </div>
                    <h3 className="font-semibold text-xl text-gray-900 mb-1">{section.label}</h3>
                    <p className="text-gray-600">{section.description}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default AdminDashboard;
